import { IonContent } from '@ionic/react';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import AgentChatWidget from '../../components/agentChat/AgentChatWidget';
import { AnnouncementWidget } from '../../components/announcement/AnnouncementWidget';
import { TabbedCratesWidget } from '../../components/crates/widgets/CrateWidgets';
import IntakeBar from '../../components/dashboard/intake/IntakeBar';
import {
  CanvasProvider,
  useCanvas,
} from '../../components/canvas/CanvasContext';
import CanvasSurface from '../../components/canvas/CanvasSurface';
import CanvasTabs from '../../components/canvas/CanvasTabs';
import useRestoreHomePageLayout from '../../components/canvas/useRestoreHomePageLayout';
import { ScrollContext } from '../../components/scrollContext/ScrollContext';
import { TrackRecommendationsWidget } from '../../components/tracks/widgets/TrackRecommendationsWidget';
import { useLibraryStats } from '../../context/LibraryStatsContext';
import { LibraryOverviewWidget } from '../../pages/my-library/widgets/LibraryWidgets';
import useDashboardShortcuts from '../../hooks/useDashboardShortcuts';
import { subscribeToAgentCanvas } from '../../services/AgentCanvasFeed.service';
import { clearCrate } from '../../store/slices/crate.slice';
import { clearTrackMatches } from '../../store/slices/track-matcher.slice';
import { AppDispatch } from '../../store/store';
import isElectron from '../../utils/isElectron';
import GetStartedStrip from './GetStartedStrip';
import './DashboardPage.scss';

const DashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { refreshStats } = useLibraryStats();
  useDashboardShortcuts();

  useEffect(() => {
    dispatch(clearCrate());
    dispatch(clearTrackMatches());
    if (isElectron()) {
      try {
        refreshStats();
      } catch (err) {
        console.error('Failed to refresh library stats on dashboard', err);
      }
    }
    // Mount-only: refreshStats is recreated on every stats update, so listing
    // it here would request statistics in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const context = useContext(ScrollContext);
  if (!context) return <></>;
  const { tutorialSectionRef, highlightTutorialSection } = context;

  return (
    <IonContent fullscreen scrollY={false} className="dashboard-page">
      <CanvasProvider>
        <div className="homepage-shell">
          <div className="homepage-main">
            <CanvasTabs />
            <HomepageBody
              tutorialSectionRef={tutorialSectionRef}
              highlightTutorialSection={highlightTutorialSection}
            />
          </div>
          <aside className="homepage-chat">
            <AgentChatWidget editing={false} />
          </aside>
        </div>
      </CanvasProvider>
    </IonContent>
  );
};

/**
 * Home stays mounted behind an open canvas rather than unmounting, so its
 * scroll position and already-loaded widgets are still there on the way back.
 */
const HomepageBody: React.FC<{
  tutorialSectionRef: React.RefObject<HTMLDivElement>;
  highlightTutorialSection: boolean;
}> = ({ tutorialSectionRef, highlightTutorialSection }) => {
  const canvas = useCanvas();
  const canvasOpen = Boolean(canvas?.activeId);
  useRestoreHomePageLayout(canvasOpen);

  useEffect(() => {
    if (!canvas) return undefined;
    return subscribeToAgentCanvas((tab) => {
      canvas.openCanvas(tab);
    });
  }, [canvas]);

  return (
    <div className="homepage-viewport">
      <div className={`homepage-content${canvasOpen ? ' is-behind' : ''}`}>
        <IntakeBar />
        <LibraryOverviewWidget editing={false} />
        {/* Tutorials stay ahead of the widgets and announcements: an active
            promotion must not push the onboarding videos out of first view. */}
        <div
          ref={tutorialSectionRef}
          className={`homepage-started${
            highlightTutorialSection ? ' get-started-section-highlight' : ''
          }`}
        >
          <GetStartedStrip />
        </div>
        <div className="homepage-grid">
          <TrackRecommendationsWidget editing={false} />
          <TabbedCratesWidget editing={false} numShown={4} />
          <div className="homepage-announcements">
            <AnnouncementWidget editing={false} limit={3} fallback={false} />
          </div>
        </div>
      </div>
      <CanvasSurface />
    </div>
  );
};

export default DashboardPage;
